import { create } from 'zustand';

import type { GetAssistantSessionsContext200 as SessionContextResponse } from '@/api/generated/lucioAPI.schemas';
import {
  type OutlookContext,
  getCurrentEmailItem,
  getOutlookContext,
  isReadingDraft,
} from '@/config/office/office';
import type { EmailItem } from '@/config/office/types';

export type FilterMode = 'currentEmailThread' | 'allMails' | 'custom' | 'folder';

interface OfficeContextState {
  activeSessionContext?:
    | {
        context?: string;
        conversationId?: string;
        draftId?: string;
        emailId?: string;
      }
    | undefined;
  contextEmailIds: Array<string>;
  contextInfo: OutlookContext;
  currentEmail: EmailItem | null;
  currentSessionId: string | null;
  fetchCurrentEmail: () => Promise<void>;
  getCurrentFilterMode: () => FilterMode;
  // Computed selectors
  getDefaultFilterMode: () => FilterMode;
  hasActiveEmail: () => boolean;
  hasActiveThread: () => boolean;

  // Actions
  initialize: () => Promise<void>;
  isInitialized: boolean;
  // This state means the associated session in BE relevant to the open email/conversation in outlook
  outlookContextLinkedSession: SessionContextResponse | undefined;
  refreshContext: () => Promise<void>;
  reset: () => void;
  selectedCustomEmails: Array<string>;
  selectedFilterMode: FilterMode | null;
  selectedFolderId: string | null;
  selectedFolderName: string | null;

  setContextEmailIds: (ids: Array<string>) => void;
  setCurrentSessionId: (sessionId: string | null) => void;
  setOutlookContextLinkedSession: (context: SessionContextResponse | undefined) => void;
  setSelectedCustomEmails: (ids: string[] | ((prev: Array<string>) => Array<string>)) => void;
  setSelectedFilterMode: (mode: FilterMode | null) => void;
  setSelectedFolder: (folderId: string | null, folderName: string | null) => void;
  setSessionContext: (context?: {
    context?: string;
    conversationId?: string;
    draftId?: string;
    emailId?: string;
  }) => void;
}

// Utility function to determine initial filter mode
export const determineInitialFilterMode = ({
  // This is what the BE sent us
  activeSessionContext,
  contextInfo,
  selectedCustomEmails,
  // Use this just as a fallback
  currentMode,
}: {
  activeSessionContext?:
    | {
        context?: string | undefined;
        conversationId?: string | undefined;
        draftId?: string | undefined;
        emailId?: string | undefined;
      }
    | undefined;
  contextInfo: OutlookContext;
  currentMode: FilterMode;
  selectedCustomEmails: Array<string>;
}): FilterMode => {
  const { conversationId, draftId, emailId } = activeSessionContext ?? {};

  // 1. Trust Backend: If session context exists, use it directly
  if (activeSessionContext?.context) {
    switch (activeSessionContext.context) {
      case 'CUSTOM':
        return 'custom';
      case 'FOLDER':
        return 'folder';
      case 'THREAD':
        // If viewing the same thread, show "Current Email/Thread"
        if (contextInfo.conversationId === conversationId) {
          return 'currentEmailThread';
        }
        return 'custom';
      case 'EMAIL':
        // If viewing the same email/draft, show "Current Email/Thread"
        if (contextInfo.itemId === emailId || (draftId && contextInfo.itemId === draftId)) {
          return 'currentEmailThread';
        }
        return 'custom';
      case 'INBOX':
        return 'allMails';
      default:
        break;
    }
  }

  // 2. Fallback / New Session Logic (No active session context)

  // If user has manually selected custom emails, force custom
  if (selectedCustomEmails.length > 0) {
    return 'custom';
  }

  // Otherwise respect the last user-selected mode or default
  return currentMode;
};

export const useOfficeContextStore = create<OfficeContextState>()((set, get) => ({
  // Initial state
  activeSessionContext: undefined,
  contextEmailIds: [],
  contextInfo: {
    conversationId: null,
    itemId: null,
    subject: null,
    type: 'none',
  },
  currentEmail: null,
  currentSessionId: null,
  isInitialized: false,
  outlookContextLinkedSession: undefined,
  selectedCustomEmails: [],
  selectedFilterMode: null,
  selectedFolderId: null,
  selectedFolderName: null,

  // Actions
  fetchCurrentEmail: async () => {
    const email = await getCurrentEmailItem();
    if (email) {
      set({ currentEmail: email });
    }
  },
  initialize: async () => {
    const context = getOutlookContext();

    set({
      contextInfo: context,
      isInitialized: true,
    });

    // If it's an email with an itemId, fetch details
    if (context.type === 'email' && context.itemId) {
      await get().fetchCurrentEmail();
    }
  },
  refreshContext: async () => {
    try {
      const context = getOutlookContext();
      const currentContext = get().contextInfo;

      // Only update if context actually changed
      if (
        context.itemId !== currentContext.itemId ||
        context.conversationId !== currentContext.conversationId ||
        context.type !== currentContext.type
      ) {
        set({
          contextInfo: context,
        });

        // If it's an email with an itemId, fetch details
        if (context.type === 'email' && context.itemId) {
          try {
            await get().fetchCurrentEmail();
          } catch (error) {
            console.error('Error fetching current email:', error);
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing context:', error);
      // Don't throw - just log the error to prevent add-in from closing
    }
  },
  reset: () => {
    set({
      activeSessionContext: undefined,
      contextEmailIds: [],
      contextInfo: {
        conversationId: null,
        itemId: null,
        subject: null,
        type: 'none',
      },
      currentEmail: null,
      currentSessionId: null,
      isInitialized: false,
      outlookContextLinkedSession: undefined,
      selectedCustomEmails: [],
      selectedFilterMode: null,
      selectedFolderId: null,
      selectedFolderName: null,
    });
  },
  setContextEmailIds: (ids) => set({ contextEmailIds: ids }),

  setCurrentSessionId: (sessionId: string | null) => {
    set({ currentSessionId: sessionId });
  },

  setSelectedCustomEmails: (ids) => {
    if (typeof ids === 'function') {
      set((state) => ({
        selectedCustomEmails: ids(state.selectedCustomEmails),
      }));
    } else {
      set({ selectedCustomEmails: ids });
    }
  },

  setSelectedFilterMode: (mode) => set({ selectedFilterMode: mode }),

  setSelectedFolder: (folderId, folderName) =>
    set({ selectedFolderId: folderId, selectedFolderName: folderName }),

  setSessionContext: (context) => set({ activeSessionContext: context }),

  // Computed selectors
  getCurrentFilterMode: () => {
    const { activeSessionContext, contextInfo, selectedCustomEmails, selectedFilterMode } = get();

    // Prioritize user's manual selection if explicitly set
    // This ensures the tab text reflects what the user selected
    if (selectedFilterMode !== null) {
      return selectedFilterMode;
    }

    // Otherwise, use the smart logic to determine mode based on context
    return determineInitialFilterMode({
      activeSessionContext,
      contextInfo,
      currentMode: get().getDefaultFilterMode(),
      selectedCustomEmails,
    });
  },

  getDefaultFilterMode: () => {
    const { hasActiveEmail, hasActiveThread } = get();

    // If we have an itemId, it's an existing email (Read mode)
    if (hasActiveEmail() || hasActiveThread()) {
      return 'currentEmailThread';
    }

    // Compose mode or no item selected
    return 'allMails';
  },

  hasActiveEmail: () => {
    const { contextInfo } = get();
    return contextInfo.type === 'email' && contextInfo.itemId !== null;
  },

  hasActiveThread: () => {
    const { contextInfo } = get();
    return (
      (contextInfo.type === 'email' && contextInfo.conversationId !== null) || isReadingDraft()
    );
  },

  setOutlookContextLinkedSession: (context: SessionContextResponse | undefined) => {
    set({ outlookContextLinkedSession: context });
  },
}));
